import { createApp } from 'vue'
import App from './App.vue'

import router from './router'
import store from './store'
import { configureRestaurantPrintTransport } from '@/services/printing/restaurantPrintTransport';
import { initAndApplyRestaurantCustoms, applyRestaurantCustoms  } from '@/utils/restaurant_customs';
import './registerServiceWorker';
import { initLocalDb } from '@/services/localDb';
import { initLocalDevice } from '@/services/localDb/device';
import {
  setOfflineRuntimeEnabled,
  startOfflineRuntime,
} from '@/services/offline/offlineRuntime';
import { hydrateOfflineSessionStore } from '@/services/offline/offlineUserAccess';
import { runOfflineSecurityMigration } from '@/services/offline/restaurantOfflineData';
import { OFFLINE_MODE_ENABLED } from '@/services/offline/offlineFeature';
import { hydrateRestaurantCustoms } from '@/utils/restaurant_customs';
import { WAITER_MODE_STATE_KEY, getPersistedAppState, setPersistedAppState } from '@/services/offline/appState';
import '@/services/http/deviceHeaderInterceptor';
import { configureDeviceResponseInterceptor } from '@/services/http/deviceHeaderInterceptor';
import { installStaffPinInterceptor } from '@/services/staff/staffActionService';
import {
  startDeviceHeartbeat,
  stopDeviceHeartbeat,
} from '@/services/device/deviceHeartbeatService';
import { configureDeviceOperationalPolicy } from '@/services/device/deviceOperationalPolicy';
import {
  startLocalControlRuntime,
  stopLocalControlRuntime,
} from '@/services/localHub/localControlRuntime';
import { synchronizePrintAgentSession } from '@/services/printing/printAgentService';
import '@fortawesome/fontawesome-free/css/all.min.css';



//import vue3GoogleLogin from 'vue3-google-login'
//import gAuthPlugin from 'vue3-google-oauth2';
//import GoogleSignInPlugin from "vue3-google-signin"

import ElementPlus from 'element-plus'
import 'element-plus/dist/index.css'

import 'fontsource-montserrat/700.css';

import 'vuetify/styles'
import { createVuetify } from 'vuetify'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'
import '@mdi/font/css/materialdesignicons.css';

const vuetify = createVuetify({
  components,
  directives,
  icons: {
    iconfont: 'mdi', // Utiliza el conjunto de íconos de Material Design
  },
})

import "jquery"
import 'bootstrap/dist/css/bootstrap.min.css'
import 'bootstrap'
//import 'bootstrap-vue/dist/bootstrap-vue.css';
//import BootstrapVue from 'bootstrap-vue';
import "axios"

import 'bootstrap-icons/font/bootstrap-icons.css'; // Importa el archivo CSS de Bootstrap Icons
import 'bootstrap-icons/font/bootstrap-icons';

import HighchartsVue from 'highcharts-vue';
import { getPartnerToken, getPartnerRefresh } from '@/services/auth/partnerSessionStorage';

function readStoredToken(key) {
  if (key === 'partner_token') return getPartnerToken();
  if (key === 'refresh') return getPartnerRefresh();
  const sources = [localStorage, sessionStorage];

  for (const source of sources) {
    try {
      const value = source.getItem(key);
      if (value && value !== 'null' && value !== 'undefined') {
        return value;
      }
    } catch (error) {
      console.warn(`Unable to read ${key} from storage:`, error);
    }
  }

  return null;
}

const token = readStoredToken('token');
const partnerToken = readStoredToken('partner_token');

configureRestaurantPrintTransport(() => ({
  token: store.state.partner_token,
  mode: store.state.operationalMode?.mode || 'CLOUD_MODE',
}));
const app = createApp(App)
    .use(store)
    .use(router)
    .use(HighchartsVue)
    .use(vuetify)
    .use(ElementPlus, {
      compilerOptions: { isCustomElement: tag => tag.startsWith('el-') },
    })

configureDeviceResponseInterceptor(store);
// PIN de empleados: después del de dispositivo, para recibir ya el 428 del servidor.
installStaffPinInterceptor();
configureDeviceOperationalPolicy(() => store.state.device);

function routeUsesPartnerNetwork(route = router.currentRoute.value) {
  return Boolean(
    route?.meta?.partnerMenu
    || route?.meta?.tpvHub
    || route?.meta?.showFooterWaiter,
  );
}

function stopPartnerNetworkRuntime() {
  stopDeviceHeartbeat();
  stopLocalControlRuntime();
}

let partnerRuntimeGeneration = 0;
let routeRuntimeReady = false;

async function startPartnerNetworkRuntime(generation) {
  try {
    const livePartnerToken = readStoredToken('partner_token');
    if (
      !livePartnerToken
      || !routeUsesPartnerNetwork()
      || generation !== partnerRuntimeGeneration
    ) return;
    void synchronizePrintAgentSession({
      backendUrl: process.env.VUE_APP_API_URL,
      partnerToken: livePartnerToken,
      refreshToken: readStoredToken('refresh'),
    });
    try { await store.dispatch('device/loadCurrentDevice'); } catch { /* Online session may need refresh first. */ }
    if (generation !== partnerRuntimeGeneration || !routeUsesPartnerNetwork()) return;
    try { await store.dispatch('device/initializeOperationalAccess'); } catch { /* Offline secondary remains blocked. */ }
    if (generation !== partnerRuntimeGeneration || !routeUsesPartnerNetwork()) return;
    await startDeviceHeartbeat(store);
    if (!OFFLINE_MODE_ENABLED) return;
    await startLocalControlRuntime(store).catch((error) => {
      console.warn('EWU local control initialization failed:', error);
    });
    if (generation !== partnerRuntimeGeneration || !routeUsesPartnerNetwork()) {
      stopPartnerNetworkRuntime();
      return;
    }
    await store.dispatch('operationalMode/initialize').catch((error) => {
      if (store.state.operationalMode.mode === 'LOCAL_MODE') {
        console.warn('EWU Local Hub unavailable while local authority is active:', error);
      }
    });
  } catch (error) {
    console.warn('EWU TPV network runtime initialization failed:', error);
  }
}

function synchronizeRouteRuntimes(route = router.currentRoute.value) {
  const isAdminLogin = route?.name === 'LoginAdmin';
  setOfflineRuntimeEnabled(!isAdminLogin);
  partnerRuntimeGeneration += 1;
  const generation = partnerRuntimeGeneration;
  if (!routeRuntimeReady) {
    if (!routeUsesPartnerNetwork(route)) stopPartnerNetworkRuntime();
    return;
  }
  if (!routeUsesPartnerNetwork(route)) {
    stopPartnerNetworkRuntime();
    return;
  }
  void startPartnerNetworkRuntime(generation);
}

router.afterEach((to) => {
  synchronizeRouteRuntimes(to);
});

async function bootstrapApp() {
  const restaurant = router.currentRoute.value.params.restaurant;
  const shouldFetchCustoms = Boolean(token && !partnerToken && navigator.onLine);

  try {
    await Promise.all([
      OFFLINE_MODE_ENABLED ? hydrateOfflineSessionStore() : Promise.resolve(null),
      hydrateRestaurantCustoms(),
    ]);
    const waiterMode = await getPersistedAppState(WAITER_MODE_STATE_KEY);

    if (waiterMode !== null && waiterMode !== undefined) {
      store.commit('setWaiterMode', Boolean(waiterMode));
    } else {
      const legacyWaiterMode = sessionStorage.getItem('isWaiterMode');
      if (legacyWaiterMode !== null) {
        const normalizedWaiterMode = legacyWaiterMode === 'true';
        await setPersistedAppState(WAITER_MODE_STATE_KEY, normalizedWaiterMode);
        store.commit('setWaiterMode', normalizedWaiterMode);
      }
    }

  } catch (error) {
    console.warn('EWU bootstrap hydration failed:', error);
  }

  if (shouldFetchCustoms) {
    void initAndApplyRestaurantCustoms(token, restaurant).catch((error) => {
      console.warn('EWU TPV remote customization bootstrap failed:', error);
    });
  }

  applyRestaurantCustoms();

  try {
    await store.dispatch('device/initializeDeviceIdentity');
    const db = await initLocalDb();
    if (db) {
      await initLocalDevice();
      if (OFFLINE_MODE_ENABLED) await runOfflineSecurityMigration();
    }
  } catch (error) {
    console.warn('EWU TPV local storage initialization failed:', error);
  }

  setOfflineRuntimeEnabled(router.currentRoute.value.name !== 'LoginAdmin');
  app.mount('#app');
  startOfflineRuntime();
  routeRuntimeReady = true;
  synchronizeRouteRuntimes();
}

router.isReady().then(() => {
  void bootstrapApp();
});
